import { Button } from "@/components/ui/button";
import type { SalesDisplaySlide } from "@/lib/sales-display-slides";
import { KIND_LABELS, WIDGET_OPTIONS } from "./slide-form-state";

interface Props {
  slides: SalesDisplaySlide[];
  isLoading: boolean;
  isReordering: boolean;
  isDeleting: boolean;
  onMove: (index: number, direction: -1 | 1) => void;
  onEdit: (slide: SalesDisplaySlide) => void;
  onDelete: (slide: SalesDisplaySlide) => void;
}

/**
 * Hvor mange skjermer raden faktisk blir på TV-en.
 *
 * Widget-rader ekspanderes av `configuredSalesDisplayLoop`: Hall of Fame blir
 * tre skjermer, salgspuls blir én pluss én per salgsenhet. Antallet for
 * salgspuls avhenger av data vi ikke har her, så lista sier at det blir flere
 * uten å gjette på hvor mange — loop-previewen over viser det eksakte tallet.
 */
function screenNote(slide: SalesDisplaySlide): string | null {
  if (slide.kind !== "widget") return null;
  if (slide.widgetKey === "hall_of_fame") return "3 skjermer";
  if (slide.widgetKey === "leaderboard") return "flere skjermer";
  return null;
}

function slideLabel(slide: SalesDisplaySlide): string {
  if (slide.kind !== "widget") return KIND_LABELS[slide.kind];
  return WIDGET_OPTIONS.find((option) => option.value === slide.widgetKey)?.label ?? "Salgswidget";
}

export function SalesDisplaySlideList({
  slides,
  isLoading,
  isReordering,
  isDeleting,
  onMove,
  onEdit,
  onDelete,
}: Props) {
  if (isLoading) return <div className="h-24 animate-pulse rounded bg-muted" />;
  if (slides.length === 0) {
    return <p className="text-sm text-muted-foreground">Ingen slides er opprettet ennå.</p>;
  }

  return (
    <>
      {slides.map((slide, index) => {
        const note = screenNote(slide);
        return (
          <div
            key={slide.id}
            data-testid="sales-display-slide-row"
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-3"
          >
            <div>
              <p className="font-medium">
                {index + 1}. {slide.title || slideLabel(slide)}
              </p>
              <p className="text-xs text-muted-foreground">
                {slideLabel(slide)} · {slide.durationSeconds} sek
                {note ? ` · ${note}` : ""} · {slide.isActive ? "Aktiv" : "Skjult"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onMove(index, -1)}
                disabled={index === 0 || isReordering}
              >
                Opp
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onMove(index, 1)}
                disabled={index === slides.length - 1 || isReordering}
              >
                Ned
              </Button>
              <Button size="sm" variant="outline" onClick={() => onEdit(slide)}>
                Rediger
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={isDeleting}
                onClick={() => onDelete(slide)}
              >
                Slett
              </Button>
            </div>
          </div>
        );
      })}
    </>
  );
}
